// cookieconsent-config.js
import 'vanilla-cookieconsent/dist/cookieconsent.css'
import * as CookieConsent from 'vanilla-cookieconsent'

const CAT_NECESSARY = 'necessary'
const CAT_ANALYTICS = 'analytics'
const CAT_ADVERTISEMENT = 'advertisement'
const CAT_FUNCTIONALITY = 'functionality'
const CAT_SECURITY = 'security'

const SERVICE_AD_STORAGE = 'ad_storage'
const SERVICE_AD_USER_DATA = 'ad_user_data'
const SERVICE_AD_PERSONALIZATION = 'ad_personalization'
const SERVICE_ANALYTICS_STORAGE = 'analytics_storage'
const SERVICE_FUNCTIONALITY_STORAGE = 'functionality_storage'
const SERVICE_PERSONALIZATION_STORAGE = 'personalization_storage'
const SERVICE_SECURITY_STORAGE = 'security_storage'

// Define dataLayer and the gtag function.
window.dataLayer = window.dataLayer || []
function gtag() {
    dataLayer.push(arguments)
}

// Set default consent to 'denied' (this should happen before changing any other dataLayer)
gtag('consent', 'default', {
    [SERVICE_AD_STORAGE]: 'denied',
    [SERVICE_AD_USER_DATA]: 'denied',
    [SERVICE_AD_PERSONALIZATION]: 'denied',
    [SERVICE_ANALYTICS_STORAGE]: 'denied',
    [SERVICE_FUNCTIONALITY_STORAGE]: 'denied',
    [SERVICE_PERSONALIZATION_STORAGE]: 'denied',
    [SERVICE_SECURITY_STORAGE]: 'denied'
})

/**
 * Update gtag consent according to the users choices made in CookieConsent UI
 */
function updateGtagConsent() {
    gtag('consent', 'update', {
        [SERVICE_ANALYTICS_STORAGE]: CookieConsent.acceptedService(
            SERVICE_ANALYTICS_STORAGE,
            CAT_ANALYTICS
        )
            ? 'granted'
            : 'denied',
        [SERVICE_AD_STORAGE]: CookieConsent.acceptedService(
            SERVICE_AD_STORAGE,
            CAT_ADVERTISEMENT
        )
            ? 'granted'
            : 'denied',
        [SERVICE_AD_USER_DATA]: CookieConsent.acceptedService(
            SERVICE_AD_USER_DATA,
            CAT_ADVERTISEMENT
        )
            ? 'granted'
            : 'denied',
        [SERVICE_AD_PERSONALIZATION]: CookieConsent.acceptedService(
            SERVICE_AD_PERSONALIZATION,
            CAT_ADVERTISEMENT
        )
            ? 'granted'
            : 'denied',
        [SERVICE_FUNCTIONALITY_STORAGE]: CookieConsent.acceptedService(
            SERVICE_FUNCTIONALITY_STORAGE,
            CAT_FUNCTIONALITY
        )
            ? 'granted'
            : 'denied',
        [SERVICE_PERSONALIZATION_STORAGE]: CookieConsent.acceptedService(
            SERVICE_PERSONALIZATION_STORAGE,
            CAT_FUNCTIONALITY
        )
            ? 'granted'
            : 'denied',
        [SERVICE_SECURITY_STORAGE]: CookieConsent.acceptedService(
            SERVICE_SECURITY_STORAGE,
            CAT_SECURITY
        )
            ? 'granted'
            : 'denied'
    })
}

CookieConsent.run({
    autoShow: !document
        .querySelector('body')
        .classList.contains('hide-consent'),
    // See: https://cookieconsent.orestbida.com/reference/configuration-reference.html#guioptions
    // ...

    // Trigger consent update when user choices change
    onFirstConsent: () => {
        updateGtagConsent()
    },
    onConsent: () => {
        updateGtagConsent()
    },
    onChange: () => {
        updateGtagConsent()
    },

    // Configure categories and services
    categories: {
        [CAT_NECESSARY]: {
            enabled: true, // this category is enabled by default
            readOnly: true // this category cannot be disabled
        },
        [CAT_ANALYTICS]: {
            autoClear: {
                cookies: [
                    {
                        name: /^_ga/ // regex: match all cookies starting with '_ga'
                    },
                    {
                        name: '_gid' // string: exact cookie name
                    }
                ]
            },
            // See: https://cookieconsent.orestbida.com/reference/configuration-reference.html#category-services
            services: {
                [SERVICE_ANALYTICS_STORAGE]: {
                    label: 'Enables storage (such as cookies) related to analytics e.g. visit duration.'
                }
            }
        },
        [CAT_ADVERTISEMENT]: {
            services: {
                [SERVICE_AD_STORAGE]: {
                    label: 'Enables storage (such as cookies) related to advertising.'
                },
                [SERVICE_AD_USER_DATA]: {
                    label: 'Sets consent for sending user data related to advertising to Google.'
                },
                [SERVICE_AD_PERSONALIZATION]: {
                    label: 'Sets consent for personalized advertising.'
                }
            }
        },
        [CAT_FUNCTIONALITY]: {
            services: {
                [SERVICE_FUNCTIONALITY_STORAGE]: {
                    label: 'Enables storage that supports the functionality of the website or app e.g. language settings.'
                },
                [SERVICE_PERSONALIZATION_STORAGE]: {
                    label: 'Enables storage related to personalization e.g. video recommendations.'
                }
            }
        },
        [CAT_SECURITY]: {
            services: {
                [SERVICE_SECURITY_STORAGE]: {
                    label: 'Enables storage related to security such as authentication functionality, fraud prevention, and other user protection.'
                }
            }
        }
    },

    language: {
        default: 'en',
        autoDetect: 'document',
        translations: {
            en: {
                consentModal: {
                    title: 'We use cookies',
                    description:
                        'This website uses essential cookies to ensure proper functioning and tracking cookies to understand how you interact with it. The latter are only placed after consent.',
                    acceptAllBtn: 'Accept all',
                    acceptNecessaryBtn: 'Reject all',
                    showPreferencesBtn: 'Manage individual preferences'
                },
                preferencesModal: {
                    title: 'Manage cookie preferences',
                    acceptAllBtn: 'Accept all',
                    acceptNecessaryBtn: 'Reject all',
                    savePreferencesBtn: 'Accept current selection',
                    closeIconLabel: 'Close window',
                    sections: [
                        {
                            title: 'Use of cookies',
                            description:
                                'We use cookies to guarantee the basic functions of the website and to improve your online experience.'
                        },
                        {
                            title: 'Strictly necessary cookies',
                            description:
                                'These cookies are essential for the proper functioning of the website, for example for user authentication.',
                            linkedCategory: CAT_NECESSARY
                        },
                        {
                            title: 'Analytics',
                            description:
                                'Analytics cookies help collect data that allows services to understand how users interact with a given service. These insights enable services to improve content and build better features that enhance the user experience.',
                            linkedCategory: CAT_ANALYTICS,
                            cookieTable: {
                                headers: {
                                    name: 'Name',
                                    domain: 'Service',
                                    description: 'Description',
                                    expiration: 'Expires'
                                },
                                body: [
                                    {
                                        name: '_ga',
                                        domain: 'Google Analytics',
                                        description:
                                            'Cookie placed by <a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                                        expiration: 'Expires after 12 days'
                                    },
                                    {
                                        name: '_gid',
                                        domain: 'Google Analytics',
                                        description:
                                            'Cookie placed by <a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                                        expiration: 'Session'
                                    }
                                ]
                            }
                        },
                        {
                            title: 'Advertising',
                            description:
                                'Google uses cookies for advertising, including serving and displaying ads, personalizing ads (depending on your ad settings at <a href="https://g.co/adsettings">g.co/adsettings</a>), limiting the number of times an ad is shown to a user, muting ads you have chosen not to see again, and measuring the effectiveness of ads.',
                            linkedCategory: CAT_ADVERTISEMENT
                        },
                        {
                            title: 'Functionality',
                            description:
                                'Cookies used for functionality allow users to interact with a service or site to access features that are fundamental to that service. Things considered fundamental to the service include preferences such as the user’s language selection, product optimizations that help maintain and improve a service, and retaining information related to a user’s session, such as the contents of a shopping cart.',
                            linkedCategory: CAT_FUNCTIONALITY
                        },
                        {
                            title: 'Security',
                            description:
                                'Cookies used for security authenticate users, prevent fraud, and protect users during their interaction with a service.',
                            linkedCategory: CAT_SECURITY
                        }
                    ]
                }
            },
            nl: {
                consentModal: {
                    title: 'We gebruiken cookies',
                    description:
                        'Deze website gebruikt essentiële cookies om de juiste werking te garanderen en trackingcookies om te begrijpen hoe je ermee omgaat. Laatstgenoemde worden pas geplaatst na toestemming.',
                    acceptAllBtn: 'Alles accepteren',
                    acceptNecessaryBtn: 'Alles weigeren',
                    showPreferencesBtn: 'Individuele voorkeuren beheren'
                },
                preferencesModal: {
                    title: 'Cookievoorkeuren beheren',
                    acceptAllBtn: 'Alles accepteren',
                    acceptNecessaryBtn: 'Alles weigeren',
                    savePreferencesBtn: 'Huidige selectie accepteren',
                    closeIconLabel: 'Sluit venster',
                    sections: [
                        {
                            title: 'Cookiegebruik',
                            description:
                                'We gebruiken cookies om de basisfuncties van de website te garanderen en om je online ervaring te verbeteren.'
                        },
                        {
                            title: 'Strikt noodzakelijke cookies',
                            description:
                                'Deze cookies zijn essentieel voor de juiste werking van de website, bijvoorbeeld voor gebruikersauthenticatie.',
                            linkedCategory: CAT_NECESSARY
                        },
                        {
                            title: 'Analytics',
                            description:
                                'Met analytics gebruikte cookies helpen gegevens te verzamelen waarmee diensten begrijpen hoe gebruikers met een bepaalde dienst omgaan. Deze inzichten stellen diensten in staat om zowel de inhoud te verbeteren als betere functies te bouwen die de gebruikerservaring verbeteren.',
                            linkedCategory: CAT_ANALYTICS,
                            cookieTable: {
                                headers: {
                                    name: 'Naam',
                                    domain: 'Service',
                                    description: 'Beschrijving',
                                    expiration: 'Verloopt'
                                },
                                body: [
                                    {
                                        name: '_ga',
                                        domain: 'Google Analytics',
                                        description:
                                            'Cookie geplaatst door <a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                                        expiration: 'Verloopt na 12 dagen'
                                    },
                                    {
                                        name: '_gid',
                                        domain: 'Google Analytics',
                                        description:
                                            'Cookie geplaatst door <a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                                        expiration: 'Sessie'
                                    }
                                ]
                            }
                        },
                        {
                            title: 'Advertenties',
                            description:
                                'Google gebruikt cookies voor adverteren, inclusief het serveren en weergeven van advertenties, het personaliseren van advertenties (afhankelijk van je advertentie-instellingen op <a href="https://g.co/adsettings">g.co/adsettings</a>), het beperken van het aantal keren dat een advertentie aan een gebruiker wordt getoond, het dempen van advertenties die je hebt gekozen niet meer te zien, en het meten van de effectiviteit van advertenties.',
                            linkedCategory: CAT_ADVERTISEMENT
                        },
                        {
                            title: 'Functionaliteit',
                            description:
                                'Cookies die voor functionaliteit worden gebruikt, stellen gebruikers in staat te interageren met een dienst of site om toegang te krijgen tot functies die fundamenteel zijn voor die dienst. Zaken die als fundamenteel worden beschouwd voor de dienst zijn onder andere voorkeuren zoals de taalselectie van de gebruiker, productoptimalisaties die helpen een dienst te behouden en te verbeteren, en het behouden van informatie met betrekking tot de sessie van een gebruiker, zoals de inhoud van een winkelwagen.',
                            linkedCategory: CAT_FUNCTIONALITY
                        },
                        {
                            title: 'Beveiliging',
                            description:
                                'Cookies die voor beveiliging worden gebruikt, authenticeren gebruikers, voorkomen fraude en beschermen gebruikers tijdens hun interactie met een dienst.',
                            linkedCategory: CAT_SECURITY
                        }
                    ]
                }
            },

            de: {
                consentModal: {
                    title: 'Wir verwenden Cookies',
                    description:
                        'Diese Website verwendet notwendige Cookies, um den einwandfreien Betrieb sicherzustellen, und Tracking‑Cookies, um zu verstehen, wie Sie mit ihr interagieren. Letztere werden erst nach Zustimmung gesetzt.',
                    acceptAllBtn: 'Alle akzeptieren',
                    acceptNecessaryBtn: 'Alle ablehnen',
                    showPreferencesBtn: 'Individuelle Einstellungen verwalten'
                },
                preferencesModal: {
                    title: 'Cookie‑Einstellungen verwalten',
                    acceptAllBtn: 'Alle akzeptieren',
                    acceptNecessaryBtn: 'Alle ablehnen',
                    savePreferencesBtn: 'Aktuelle Auswahl akzeptieren',
                    closeIconLabel: 'Modal schließen',
                    sections: [
                        {
                            title: 'Cookie‑Verwendung',
                            description:
                                'Wir verwenden Cookies, um die grundlegenden Funktionen der Website zu gewährleisten und Ihre Online‑Erfahrung zu verbessern.'
                        },
                        {
                            title: 'Unbedingt erforderliche Cookies',
                            description:
                                'Diese Cookies sind für das ordnungsgemäße Funktionieren der Website unerlässlich, zum Beispiel für die Benutzerauthentifizierung.',
                            linkedCategory: CAT_NECESSARY
                        },
                        {
                            title: 'Analytics',
                            description:
                                'Analyse‑Cookies helfen dabei, Daten zu sammeln, die es Diensten ermöglichen, zu verstehen, wie Benutzer mit einem bestimmten Dienst interagieren. Diese Erkenntnisse ermöglichen es Diensten, Inhalte zu verbessern und bessere Funktionen zu entwickeln, die die Benutzererfahrung verbessern.',
                            linkedCategory: CAT_ANALYTICS,
                            cookieTable: {
                                headers: {
                                    name: 'Name',
                                    domain: 'Dienst',
                                    description: 'Beschreibung',
                                    expiration: 'Ablauf'
                                },
                                body: [
                                    {
                                        name: '_ga',
                                        domain: 'Google Analytics',
                                        description:
                                            'Cookie gesetzt von <a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                                        expiration: 'Läuft nach 12 Tagen ab'
                                    },
                                    {
                                        name: '_gid',
                                        domain: 'Google Analytics',
                                        description:
                                            'Cookie gesetzt von <a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                                        expiration: 'Sitzung'
                                    }
                                ]
                            }
                        },
                        {
                            title: 'Werbung',
                            description:
                                'Google verwendet Cookies für Werbung, einschließlich Auslieferung und Darstellung von Anzeigen, Personalisierung von Anzeigen (abhängig von Ihren Anzeigeeinstellungen unter <a href="https://g.co/adsettings">g.co/adsettings</a>), Begrenzung der Anzahl der Anzeigen, die einem Nutzer angezeigt werden, Stummschaltung von Anzeigen, die Sie nicht mehr sehen möchten, und Messung der Wirksamkeit von Anzeigen.',
                            linkedCategory: CAT_ADVERTISEMENT
                        },
                        {
                            title: 'Funktionalität',
                            description:
                                'Cookies für Funktionalität ermöglichen es Nutzern, mit einem Dienst oder einer Seite zu interagieren, um auf Funktionen zuzugreifen, die für diesen Dienst grundlegend sind. Zu den als grundlegend für den Dienst betrachteten Dingen gehören Einstellungen wie die Sprachwahl des Nutzers, Produktoptimierungen, die helfen, einen Dienst aufrechtzuerhalten und zu verbessern, sowie das Beibehalten von Sitzungsinformationen eines Nutzers, wie dem Inhalt eines Warenkorbs.',
                            linkedCategory: CAT_FUNCTIONALITY
                        },
                        {
                            title: 'Sicherheit',
                            description:
                                'Cookies zur Sicherheit authentifizieren Nutzer, verhindern Betrug und schützen Nutzer bei der Interaktion mit einem Dienst.',
                            linkedCategory: CAT_SECURITY
                        }
                    ]
                }
            },

            it: {
                consentModal: {
                    title: 'Utilizziamo i cookie',
                    description:
                        'Questo sito web utilizza cookie essenziali per garantirne il corretto funzionamento e cookie di tracciamento per capire come interagisci con esso. Questi ultimi verranno impostati solo dopo il consenso.',
                    acceptAllBtn: 'Accetta tutto',
                    acceptNecessaryBtn: 'Rifiuta tutto',
                    showPreferencesBtn: 'Gestisci preferenze individuali'
                },
                preferencesModal: {
                    title: 'Gestisci preferenze cookie',
                    acceptAllBtn: 'Accetta tutto',
                    acceptNecessaryBtn: 'Rifiuta tutto',
                    savePreferencesBtn: 'Accetta selezione corrente',
                    closeIconLabel: 'Chiudi finestra',
                    sections: [
                        {
                            title: 'Utilizzo dei cookie',
                            description:
                                'Utilizziamo i cookie per garantire le funzionalità di base del sito web e migliorare la tua esperienza online.'
                        },
                        {
                            title: 'Cookie strettamente necessari',
                            description:
                                "Questi cookie sono essenziali per il corretto funzionamento del sito, ad esempio per l'autenticazione dell'utente.",
                            linkedCategory: CAT_NECESSARY
                        },
                        {
                            title: 'Analisi',
                            description:
                                "I cookie di analisi aiutano a raccogliere dati che consentono ai servizi di capire come gli utenti interagiscono con un determinato servizio. Queste informazioni consentono ai servizi di migliorare i contenuti e sviluppare funzionalità migliori per migliorare l'esperienza dell'utente.",
                            linkedCategory: CAT_ANALYTICS,
                            cookieTable: {
                                headers: {
                                    name: 'Nome',
                                    domain: 'Servizio',
                                    description: 'Descrizione',
                                    expiration: 'Scadenza'
                                },
                                body: [
                                    {
                                        name: '_ga',
                                        domain: 'Google Analytics',
                                        description:
                                            'Cookie impostato da <a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                                        expiration: 'Scade dopo 12 giorni'
                                    },
                                    {
                                        name: '_gid',
                                        domain: 'Google Analytics',
                                        description:
                                            'Cookie impostato da <a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                                        expiration: 'Sessione'
                                    }
                                ]
                            }
                        },
                        {
                            title: 'Pubblicità',
                            description:
                                'Google utilizza cookie per la pubblicità, incluso il servizio e la visualizzazione di annunci, la personalizzazione degli annunci (in base alle tue impostazioni su <a href="https://g.co/adsettings">g.co/adsettings</a>), la limitazione del numero di volte in cui un annuncio viene mostrato a un utente, il silenziamento degli annunci che hai scelto di non vedere e la misurazione dell\'efficacia degli annunci.',
                            linkedCategory: CAT_ADVERTISEMENT
                        },
                        {
                            title: 'Funzionalità',
                            description:
                                "I cookie per funzionalità consentono agli utenti di interagire con un servizio o sito per accedere a funzionalità fondamentali per quel servizio. Considerate fondamentali per il servizio sono preferenze come la scelta della lingua dell'utente, ottimizzazioni del prodotto che aiutano a mantenere e migliorare un servizio e il mantenimento delle informazioni relative alla sessione di un utente, come il contenuto di un carrello.",
                            linkedCategory: CAT_FUNCTIONALITY
                        },
                        {
                            title: 'Sicurezza',
                            description:
                                "I cookie per la sicurezza autenticano gli utenti, prevengono frodi e proteggono gli utenti durante l'interazione con un servizio.",
                            linkedCategory: CAT_SECURITY
                        }
                    ]
                }
            },

            es: {
                consentModal: {
                    title: 'Utilizamos cookies',
                    description:
                        'Este sitio web utiliza cookies esenciales para garantizar su correcto funcionamiento y cookies de seguimiento para comprender cómo interactúas con él. Estas últimas se establecerán solo tras el consentimiento.',
                    acceptAllBtn: 'Aceptar todo',
                    acceptNecessaryBtn: 'Rechazar todo',
                    showPreferencesBtn: 'Gestionar preferencias individuales'
                },
                preferencesModal: {
                    title: 'Gestionar preferencias de cookies',
                    acceptAllBtn: 'Aceptar todo',
                    acceptNecessaryBtn: 'Rechazar todo',
                    savePreferencesBtn: 'Aceptar selección actual',
                    closeIconLabel: 'Cerrar modal',
                    sections: [
                        {
                            title: 'Uso de cookies',
                            description:
                                'Utilizamos cookies para garantizar las funcionalidades básicas del sitio web y mejorar tu experiencia en línea.'
                        },
                        {
                            title: 'Cookies estrictamente necesarias',
                            description:
                                'Estas cookies son esenciales para el correcto funcionamiento del sitio web, por ejemplo para la autenticación de usuarios.',
                            linkedCategory: CAT_NECESSARY
                        },
                        {
                            title: 'Analítica',
                            description:
                                'Las cookies de analítica ayudan a recopilar datos que permiten a los servicios entender cómo los usuarios interactúan con un servicio determinado. Estos conocimientos permiten a los servicios mejorar el contenido y crear mejores funciones que mejoren la experiencia del usuario.',
                            linkedCategory: CAT_ANALYTICS,
                            cookieTable: {
                                headers: {
                                    name: 'Nombre',
                                    domain: 'Servicio',
                                    description: 'Descripción',
                                    expiration: 'Caducidad'
                                },
                                body: [
                                    {
                                        name: '_ga',
                                        domain: 'Google Analytics',
                                        description:
                                            'Cookie establecida por <a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                                        expiration: 'Expira tras 12 días'
                                    },
                                    {
                                        name: '_gid',
                                        domain: 'Google Analytics',
                                        description:
                                            'Cookie establecida por <a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                                        expiration: 'Sesión'
                                    }
                                ]
                            }
                        },
                        {
                            title: 'Publicidad',
                            description:
                                'Google utiliza cookies para publicidad, incluyendo el servicio y la visualización de anuncios, la personalización de anuncios (según tus ajustes en <a href="https://g.co/adsettings">g.co/adsettings</a>), la limitación de la cantidad de veces que se muestra un anuncio a un usuario, la desactivación de anuncios que has elegido no ver y la medición de la eficacia de los anuncios.',
                            linkedCategory: CAT_ADVERTISEMENT
                        },
                        {
                            title: 'Funcionalidad',
                            description:
                                'Las cookies de funcionalidad permiten a los usuarios interactuar con un servicio o sitio para acceder a funciones fundamentales de ese servicio. Se consideran fundamentales para el servicio preferencias como la elección de idioma del usuario, optimizaciones de producto que ayudan a mantener y mejorar un servicio y el mantenimiento de información relacionada con la sesión de un usuario, como el contenido de un carrito.',
                            linkedCategory: CAT_FUNCTIONALITY
                        },
                        {
                            title: 'Seguridad',
                            description:
                                'Las cookies de seguridad autentican a los usuarios, previenen fraudes y protegen a los usuarios mientras interactúan con un servicio.',
                            linkedCategory: CAT_SECURITY
                        }
                    ]
                }
            },

            fr: {
                consentModal: {
                    title: 'Nous utilisons des cookies',
                    description:
                        "Ce site utilise des cookies essentiels pour garantir son bon fonctionnement et des cookies de suivi pour comprendre comment vous interagissez avec lui. Ces derniers ne seront placés qu'après consentement.",
                    acceptAllBtn: 'Tout accepter',
                    acceptNecessaryBtn: 'Tout refuser',
                    showPreferencesBtn: 'Gérer les préférences individuelles'
                },
                preferencesModal: {
                    title: 'Gérer les préférences de cookies',
                    acceptAllBtn: 'Tout accepter',
                    acceptNecessaryBtn: 'Tout refuser',
                    savePreferencesBtn: 'Accepter la sélection actuelle',
                    closeIconLabel: 'Fermer la fenêtre',
                    sections: [
                        {
                            title: 'Utilisation des cookies',
                            description:
                                'Nous utilisons des cookies pour garantir les fonctionnalités de base du site et améliorer votre expérience en ligne.'
                        },
                        {
                            title: 'Cookies strictement nécessaires',
                            description:
                                "Ces cookies sont essentiels au bon fonctionnement du site, par exemple pour l'authentification des utilisateurs.",
                            linkedCategory: CAT_NECESSARY
                        },
                        {
                            title: 'Analytique',
                            description:
                                "Les cookies d'analyse aident à collecter des données permettant aux services de comprendre comment les utilisateurs interagissent avec un service donné. Ces informations permettent aux services d'améliorer le contenu et de créer de meilleures fonctionnalités pour améliorer l'expérience utilisateur.",
                            linkedCategory: CAT_ANALYTICS,
                            cookieTable: {
                                headers: {
                                    name: 'Nom',
                                    domain: 'Service',
                                    description: 'Description',
                                    expiration: 'Expiration'
                                },
                                body: [
                                    {
                                        name: '_ga',
                                        domain: 'Google Analytics',
                                        description:
                                            'Cookie défini par <a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                                        expiration: 'Expire après 12 jours'
                                    },
                                    {
                                        name: '_gid',
                                        domain: 'Google Analytics',
                                        description:
                                            'Cookie défini par <a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                                        expiration: 'Session'
                                    }
                                ]
                            }
                        },
                        {
                            title: 'Publicité',
                            description:
                                'Google utilise des cookies pour la publicité, notamment la diffusion et l\'affichage des annonces, la personnalisation des annonces (en fonction de vos paramètres sur <a href="https://g.co/adsettings">g.co/adsettings</a>), la limitation du nombre de fois où une annonce est affichée à un utilisateur, la mise en sourdine des annonces que vous avez choisi de ne pas voir et la mesure de l\'efficacité des annonces.',
                            linkedCategory: CAT_ADVERTISEMENT
                        },
                        {
                            title: 'Fonctionnalité',
                            description:
                                "Les cookies de fonctionnalité permettent aux utilisateurs d'interagir avec un service ou un site pour accéder à des fonctionnalités fondamentales de ce service. Les éléments considérés comme fondamentaux pour le service incluent des préférences telles que le choix de langue de l'utilisateur, des optimisations de produit qui aident à maintenir et améliorer un service, et la conservation des informations relatives à la session d'un utilisateur, comme le contenu d'un panier.",
                            linkedCategory: CAT_FUNCTIONALITY
                        },
                        {
                            title: 'Sécurité',
                            description:
                                'Les cookies de sécurité authentifient les utilisateurs, préviennent la fraude et protègent les utilisateurs lors de leur interaction avec un service.',
                            linkedCategory: CAT_SECURITY
                        }
                    ]
                }
            }
        }
    }
})
